import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTurnDownRightIcon,
  BanknotesIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline';
import { formatInTimeZone } from 'date-fns-tz';
import { getSelfUsername } from '../services/userSelf';
import {
  fetchBetConstructRevenueShare,
  listAvailableMonths,
  SPORT_REVENUE_SHARE,
  USD_RATES
} from '../services/adminStatsService';
import { fetchProviderBill } from '../services/providerBillService';
import { EUR_PER_USD } from '../utils/currency';
import { providerLabel } from '../utils/gameProviders';

const SHARE_PCT = `${(SPORT_REVENUE_SHARE * 100).toFixed(0)}%`;

const amount = (value, currency) => {
  const n = Number.isFinite(value) ? value : 0;
  const digits = currency === 'LBP' || currency === 'IQD' ? 0 : currency === 'KWD' ? 3 : 2;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const signed = (symbol) => (value) => {
  const n = Number.isFinite(value) ? value : 0;
  return `${n < 0 ? '-' : ''}${symbol}${amount(Math.abs(n), 'USD')}`;
};
const usd = signed('$');
const eur = signed('€');
const count = (value) => (Number(value) || 0).toLocaleString('en-US');
const pct = (rate) => `${Number((rate * 100).toFixed(1))}%`;
const rateLabel = (line) => (
  line.liveRate != null && line.liveRate !== line.rate
    ? `${pct(line.rate)} slots · ${pct(line.liveRate)} live`
    : pct(line.rate)
);
const ggrTone = (value) => (value < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400');
const divisorsLabel = (divisors) => Object.entries(divisors).map(([code, divisor]) => `${code} ÷ ${divisor}`).join(' · ');

const RATES_NOTE = [
  ...['LBP', 'TND', 'TRY', 'IQD', 'KWD'].map((code) => `${USD_RATES[code].toLocaleString('en-US')} ${code}`),
  `${EUR_PER_USD} EUR`
].join(' · ');

const VIEWS = [
  { id: 'share', label: `Revenue share (${SHARE_PCT})` },
  { id: 'bill', label: 'Bill' }
];

const CURRENCY_ACCENT = {
  USD: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  TND: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  LBP: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  KWD: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
};

const CurrencyCard = ({ row }) => (
  <div className="p-4 sm:p-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
    <div className="flex items-center justify-between mb-3">
      <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{row.currency}</div>
      <span className={`text-[11px] px-2 py-0.5 rounded-full ${CURRENCY_ACCENT[row.currency] || CURRENCY_ACCENT.USD}`}>
        BetConstruct
      </span>
    </div>

    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Sports GGR</div>
        <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 tabular-nums">
          {amount(row.ggr, row.currency)} {row.currency}
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Share ({SHARE_PCT})
        </div>
        <div className="text-lg sm:text-xl font-bold text-indigo-700 dark:text-indigo-300 tabular-nums">
          {amount(row.share, row.currency)} {row.currency}
        </div>
        {row.currency === 'USD' ? null : (
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            ≈ {usd(row.shareUsd)} · 1 USD = {USD_RATES[row.currency]} {row.currency}
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Players</div>
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">
            {count(row.players)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Bets</div>
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">
            {count(row.bets)}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ErrorBox = ({ title, message }) => (
  <div className="p-3 sm:p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
    <div className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">{title}</div>
    <div className="text-xs text-red-600 dark:text-red-400">{message}</div>
  </div>
);

const Pill = ({ tone = 'indigo', children }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  };
  return (
    <span className={`shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${tones[tone]}`}>{children}</span>
  );
};

const BillHero = ({ bill }) => {
  const estimated = bill.lines.some((line) => line.estimate && line.billUsd > 0);
  return (
    <div className="p-5 sm:p-6 rounded-2xl border border-indigo-200 dark:border-indigo-800/60
      bg-gradient-to-br from-indigo-50 via-white to-purple-50
      dark:from-indigo-900/20 dark:via-gray-800 dark:to-purple-900/20 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 min-w-0">
          <ReceiptPercentIcon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 text-indigo-600 dark:text-indigo-400" />
          <div className="text-[11px] sm:text-xs uppercase tracking-wider font-semibold text-indigo-700 dark:text-indigo-300 truncate">
            {bill.label} · total bill
          </div>
        </div>
        {estimated ? <Pill tone="amber">Estimate</Pill> : null}
      </div>
      <div className="text-3xl sm:text-5xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight tabular-nums">
        {usd(bill.totalUsd)}
      </div>
      <div className="mt-1 text-lg sm:text-2xl font-bold text-gray-500 dark:text-gray-400 tabular-nums">
        {eur(bill.totalEur)}
      </div>
      {bill.lines.some((line) => line.newPrice) ? (
        <div className="mt-4 pt-3 border-t border-indigo-100 dark:border-indigo-800/60 space-y-2.5">
          <div className="flex items-center gap-2.5">
            <ArrowTurnDownRightIcon className="w-4 h-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
            <div className="min-w-0 flex-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
              With BetConstruct&apos;s new price
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <div className="text-base sm:text-xl font-bold text-gray-900 dark:text-gray-100">{usd(bill.newPriceTotalUsd)}</div>
              <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">{eur(bill.newPriceTotalEur)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="w-4 shrink-0" />
            <div className="min-w-0 flex-1 text-xs sm:text-sm text-amber-800 dark:text-amber-200">
              After reduction, +30% error range
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <div className="text-base sm:text-xl font-bold text-amber-900 dark:text-amber-100">{usd(bill.newPriceWithErrorTotalUsd)}</div>
              <div className="text-[11px] sm:text-xs text-amber-700/80 dark:text-amber-300/80">{eur(bill.newPriceWithErrorTotalEur)}</div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const NewPriceStrip = ({ line }) => {
  const { newPrice } = line;
  const change = line.billUsd > 0 ? (newPrice.totalUsd - line.billUsd) / line.billUsd : null;
  const inFull = newPrice.currencies.filter((row) => !row.divided).map((row) => row.currency);
  return (
    <div className="mt-2.5 flex items-center gap-2.5 rounded-xl border border-indigo-100 dark:border-indigo-800/60
      bg-indigo-50/70 dark:bg-indigo-900/20 px-2.5 py-2">
      <ArrowTurnDownRightIcon className="w-4 h-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-indigo-900 dark:text-indigo-100">New price</span>
          {change !== null ? (
            <span className={`text-[10px] font-bold tabular-nums ${
              change < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
            }`}
            >
              {change < 0 ? '-' : '+'}{Math.abs(change * 100).toFixed(0)}%
            </span>
          ) : null}
        </div>
        <div className="text-[10px] text-indigo-700/80 dark:text-indigo-300/80 truncate">
          {pct(line.rate)} of GGR {divisorsLabel(line.newPriceDivisors)}
          {inFull.length > 0 ? ` · ${inFull.join(', ')} in full` : ''}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm sm:text-base font-bold tabular-nums text-indigo-900 dark:text-indigo-100">
          {usd(newPrice.totalUsd)}
        </div>
        <div className="text-[11px] tabular-nums text-indigo-700/80 dark:text-indigo-300/80">{eur(newPrice.totalEur)}</div>
        {newPrice.errorRange > 0 ? (
          <div className="mt-1 text-[10px] font-semibold tabular-nums text-amber-700 dark:text-amber-300">
            +{pct(newPrice.errorRange)} error range {usd(newPrice.withErrorUsd)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const BetinovioNotice = ({ coverage, startDate }) => {
  if (!coverage || coverage.status === 'complete') return null;
  const message = coverage.status === 'partial'
    ? `Betinovio is split out up to ${formatInTimeZone(new Date(coverage.through), 'Asia/Beirut', 'MMM d, HH:mm')}. ` +
      'Eva does not keep that share: only Betinovio play after that time is still inside Eva · Pragmatic Play, and this updates on its own as the scan catches up.'
    : `Betinovio can't be separated from Eva for ${formatInTimeZone(startDate, 'Asia/Beirut', 'MMMM')} yet, ` +
      'so its Pragmatic GGR is still counted under Eva · Pragmatic Play (9% slots, 12% live) instead of Betinovio (4%). The scan is catching up and this page will update.';
  return (
    <div className="flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-800
      bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-xs sm:text-sm text-amber-800 dark:text-amber-200">
      <InformationCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
      <div>{message}</div>
    </div>
  );
};

const BillLineDetail = ({ line }) => (
  <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-gray-100 dark:border-gray-700">
    {line.currencies.length === 0 ? (
      <p className="pt-3 text-xs text-gray-500 dark:text-gray-400">No GGR this month.</p>
    ) : (
      <table className="w-full mt-2 text-xs tabular-nums">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            <th className="py-1.5 text-left font-medium">Currency</th>
            <th className="py-1.5 text-right font-medium">GGR</th>
            <th className="py-1.5 text-right font-medium">≈ USD</th>
          </tr>
        </thead>
        <tbody>
          {line.currencies.map((row) => (
            <tr key={row.currency} className="border-t border-gray-100 dark:border-gray-700/60">
              <td className="py-1.5 font-semibold text-gray-700 dark:text-gray-200">{row.currency}</td>
              <td className={`py-1.5 text-right ${ggrTone(row.ggr)}`}>{amount(row.ggr, row.currency)}</td>
              <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">{usd(row.ggrUsd)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-200 dark:border-gray-600 font-semibold text-gray-900 dark:text-gray-100">
            <td className="py-1.5" colSpan={2}>Net GGR</td>
            <td className={`py-1.5 text-right ${ggrTone(line.ggrUsd)}`}>{usd(line.ggrUsd)}</td>
          </tr>
          {line.liveRate != null && line.liveRate !== line.rate ? (
            <>
              <tr className="border-t border-gray-100 dark:border-gray-700/60 text-gray-600 dark:text-gray-300">
                <td className="py-1.5" colSpan={2}>Slots · {pct(line.rate)}</td>
                <td className="py-1.5 text-right">{usd(line.slotsGgrUsd)}</td>
              </tr>
              <tr className="text-gray-600 dark:text-gray-300">
                <td className="py-1.5" colSpan={2}>Live · {pct(line.liveRate)}</td>
                <td className="py-1.5 text-right">{usd(line.liveGgrUsd)}</td>
              </tr>
            </>
          ) : null}
          <tr className="font-bold text-indigo-700 dark:text-indigo-300">
            <td className="py-1.5" colSpan={2}>Bill · {rateLabel(line)}</td>
            <td className="py-1.5 text-right">{usd(line.billUsd)}</td>
          </tr>
          <tr className="text-gray-500 dark:text-gray-400">
            <td colSpan={2}>In EUR</td>
            <td className="text-right">{eur(line.billEur)}</td>
          </tr>
        </tfoot>
      </table>
    )}

    {line.newPrice && line.currencies.length > 0 ? (
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
          <ArrowTurnDownRightIcon className="w-3.5 h-3.5" />
          New price
        </div>
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              <th className="py-1.5 text-left font-medium">Currency</th>
              <th className="py-1.5 text-right font-medium">GGR ÷</th>
              <th className="py-1.5 text-right font-medium">New GGR</th>
              <th className="py-1.5 text-right font-medium">≈ USD</th>
            </tr>
          </thead>
          <tbody>
            {line.newPrice.currencies.map((row) => (
              <tr key={row.currency} className="border-t border-gray-100 dark:border-gray-700/60">
                <td className="py-1.5 font-semibold text-gray-700 dark:text-gray-200">{row.currency}</td>
                <td className="py-1.5 text-right text-gray-500 dark:text-gray-400">{row.divided ? row.divisor : '—'}</td>
                <td className={`py-1.5 text-right ${ggrTone(row.ggr)}`}>{amount(row.ggr, row.currency)}</td>
                <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">{usd(row.ggrUsd)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 dark:border-gray-600 font-semibold text-gray-900 dark:text-gray-100">
              <td className="py-1.5" colSpan={3}>New GGR</td>
              <td className={`py-1.5 text-right ${ggrTone(line.newPrice.ggrUsd)}`}>{usd(line.newPrice.ggrUsd)}</td>
            </tr>
            <tr className="font-bold text-indigo-700 dark:text-indigo-300">
              <td className="py-1.5" colSpan={3}>New price · {pct(line.rate)}</td>
              <td className="py-1.5 text-right">{usd(line.newPrice.totalUsd)}</td>
            </tr>
            <tr className="text-gray-500 dark:text-gray-400">
              <td colSpan={3}>In EUR</td>
              <td className="text-right">{eur(line.newPrice.totalEur)}</td>
            </tr>
            {line.newPrice.errorRange > 0 ? (
              <>
                <tr className="font-bold text-amber-800 dark:text-amber-200">
                  <td className="py-1.5" colSpan={3}>+{pct(line.newPrice.errorRange)} error range</td>
                  <td className="py-1.5 text-right">{usd(line.newPrice.withErrorUsd)}</td>
                </tr>
                <tr className="text-amber-700/80 dark:text-amber-300/80">
                  <td colSpan={3}>In EUR</td>
                  <td className="text-right">{eur(line.newPrice.withErrorEur)}</td>
                </tr>
              </>
            ) : null}
          </tfoot>
        </table>
      </div>
    ) : null}

    {line.product === 'casino' && line.studios.length > 0 ? (
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="mb-1.5 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          By studio · GGR ≈ USD
        </div>
        <ul className="space-y-1">
          {line.studios.map((studio) => (
            <li key={studio.provider} className="flex items-center justify-between gap-3 text-xs">
              <span className="truncate text-gray-700 dark:text-gray-300">{providerLabel(studio.provider)}</span>
              <span className={`tabular-nums ${ggrTone(studio.ggrUsd)}`}>{usd(studio.ggrUsd)}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null}
  </div>
);

const BillLine = ({ line, open, onToggle }) => {
  const notBilled = line.billUsd < 0;
  const idle = line.currencies.length === 0;
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full p-3 sm:p-4 text-left active:bg-gray-50 dark:active:bg-gray-700/40"
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{line.label}</span>
              <Pill>{rateLabel(line)}</Pill>
              {line.estimate ? <Pill tone="amber">est.</Pill> : null}
            </div>
            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums truncate">
              {idle ? 'No activity' : (
                <>Net GGR <span className={ggrTone(line.ggrUsd)}>{usd(line.ggrUsd)}</span></>
              )}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className={`text-sm sm:text-base font-bold tabular-nums ${
              notBilled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'
            }`}
            >
              {usd(line.billUsd)}
            </div>
            <div className={`text-[11px] tabular-nums ${notBilled ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {notBilled ? 'not billed' : eur(line.billEur)}
            </div>
          </div>
          <ChevronDownIcon className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
        {line.newPrice && !idle ? <NewPriceStrip line={line} /> : null}
      </button>
      {open ? <BillLineDetail line={line} /> : null}
    </div>
  );
};

const BILL_GROUPS = [
  { id: 'sports', label: 'Sports' },
  { id: 'casino', label: 'Casino' }
];

const BillView = ({ bill, loading, error, monthName }) => {
  const [openLine, setOpenLine] = useState(null);

  if (error) return <ErrorBox title="Failed to load the bill" message={error} />;

  if (!bill) {
    return (
      <div className="py-12 flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400">
        <ArrowPathIcon className={`w-6 h-6 ${loading ? 'animate-spin' : ''}`} />
        <span className="text-sm">Calculating the {monthName} bill…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <BillHero bill={bill} />
      <BetinovioNotice coverage={bill.betinovio} startDate={bill.startDate} />

      {BILL_GROUPS.map((group) => {
        const lines = bill.lines.filter((line) => line.product === group.id);
        const subtotal = lines.reduce((total, line) => total + Math.max(0, line.billUsd), 0);
        return (
          <section key={group.id} className="space-y-2">
            <div className="flex items-baseline justify-between gap-3 px-1">
              <h2 className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {group.label}
              </h2>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 tabular-nums">{usd(subtotal)}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 items-start">
              {lines.map((line) => (
                <BillLine
                  key={line.id}
                  line={line}
                  open={openLine === line.id}
                  onToggle={() => setOpenLine((current) => (current === line.id ? null : line.id))}
                />
              ))}
            </div>
          </section>
        );
      })}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 sm:p-4
        space-y-1.5 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
        <p>
          Each line is its rate on the provider&apos;s net GGR across all currencies, at 1 USD = {RATES_NOTE}.
          A line with negative GGR isn&apos;t billed and doesn&apos;t reduce the total.
        </p>
        <p>
          Databetrix (Evolution) and Betinovio (Pragmatic Play) bill their own rate on everything they carry. Eva studio
          rates are the royalty percentages from Eva&apos;s August 2026 invoice. Pragmatic Play is 9% on slots and 12% on
          live tables. Studios missing from that invoice stay under Eva · other studios at 9%. These percentages do not
          include Eva&apos;s separate licensing or fixed fees.
        </p>
        {bill.excluded ? (
          <p>
            Not billed: weekly GGR bonus ({usd(bill.excluded.ggrUsd)}), a player bonus paid through the casino wallet
            rather than a provider round.
          </p>
        ) : null}
      </div>
    </div>
  );
};

const AdminStatistics = () => {
  const months = useMemo(() => listAvailableMonths(), []);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [selectedKey, setSelectedKey] = useState(months[0]?.key || '');
  const [view, setView] = useState('share');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bill, setBill] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const billRequest = useRef(0);
  const billRequestedFor = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const username = await getSelfUsername();
        if (cancelled) return;
        setIsAuthorized(username === 'playwinadmin');
      } catch (err) {
        console.error('[AdminStatistics] Failed to resolve self username:', err);
        if (!cancelled) setIsAuthorized(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedMonth = months.find((m) => m.key === selectedKey) || months[0];

  const loadStats = useCallback(async () => {
    if (!selectedMonth) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchBetConstructRevenueShare(selectedMonth);
      setStats(result);
      setLoadedAt(Date.now());
    } catch (err) {
      console.error('[AdminStatistics] Failed to load:', err);
      setError(err?.message || 'Failed to load statistics');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  // The bill reads a whole month of casino games, so it is only fetched once
  // the Bill view is opened; a slow month must not overwrite a newer one.
  const loadBill = useCallback(async () => {
    if (!selectedMonth) return;
    const request = billRequest.current + 1;
    billRequest.current = request;
    billRequestedFor.current = selectedMonth.key;
    setBill((current) => (current?.monthKey === selectedMonth.key ? current : null));
    setBillLoading(true);
    setBillError(null);
    try {
      const result = await fetchProviderBill(selectedMonth);
      if (billRequest.current !== request) return;
      setBill(result);
      setLoadedAt(Date.now());
    } catch (err) {
      if (billRequest.current !== request) return;
      console.error('[AdminStatistics] Failed to load bill:', err);
      billRequestedFor.current = null;
      setBillError(err?.message || 'Failed to load the bill');
      setBill(null);
    } finally {
      if (billRequest.current === request) setBillLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (authChecked && isAuthorized) loadStats();
  }, [authChecked, isAuthorized, loadStats]);

  useEffect(() => {
    if (!authChecked || !isAuthorized || view !== 'bill') return;
    if (billRequestedFor.current !== selectedMonth?.key) loadBill();
  }, [authChecked, isAuthorized, view, selectedMonth, loadBill]);

  // The Betinovio split backfills after a deploy, so a bill opened too early
  // would keep showing Eva with Betinovio's share until the next visit.
  useEffect(() => {
    if (view !== 'bill' || !bill || bill.betinovio?.status === 'complete') return undefined;
    const timer = setTimeout(loadBill, 20000);
    return () => clearTimeout(timer);
  }, [view, bill, loadBill]);

  if (authChecked && !isAuthorized) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!authChecked) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400 text-sm">
        Checking access…
      </div>
    );
  }

  const rows = stats?.currencies || [];
  const hasActivity = rows.some((r) => r.bets > 0);
  const refreshing = view === 'bill' ? billLoading : loading;

  return (
    <div className="space-y-4 sm:space-y-6 pb-24 sm:pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/30">
            <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
              Admin Statistics
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {view === 'bill'
                ? 'What each provider bills on the month’s GGR · USD and EUR'
                : `${SHARE_PCT} of BetConstruct sports GGR · USD + TND + LBP + KWD`}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={view === 'bill' ? loadBill : loadStats}
          disabled={refreshing}
          aria-label="Refresh"
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-indigo-200 dark:border-indigo-800
            text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20
            active:bg-indigo-100 dark:active:bg-indigo-900/30 disabled:opacity-60 text-sm font-medium"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Month picker */}
      <div className="p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <label className="block text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          Month
        </label>
        <select
          value={selectedKey}
          onChange={(e) => setSelectedKey(e.target.value)}
          className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600
            bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          {months.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
        <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          <CalendarDaysIcon className="w-3.5 h-3.5 shrink-0" />
          <span>Available from July 2026 · Asia/Beirut</span>
          {loadedAt ? (
            <span className="text-gray-400 dark:text-gray-500 truncate">
              · updated {new Date(loadedAt).toLocaleTimeString()}
            </span>
          ) : null}
        </div>
      </div>

      {/* View switch */}
      <div role="tablist" className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-gray-100 dark:bg-gray-800/80">
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={view === item.id}
            onClick={() => setView(item.id)}
            className={`py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors ${
              view === item.id
                ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {view === 'bill' ? (
        <BillView
          bill={bill?.monthKey === selectedMonth?.key ? bill : null}
          loading={billLoading}
          error={billError}
          monthName={selectedMonth?.label}
        />
      ) : (
        <>
          {error && <ErrorBox title="Failed to load statistics" message={error} />}

          {/* Hero — USD-convertible total */}
          <div className="p-5 sm:p-6 rounded-2xl border border-indigo-200 dark:border-indigo-800/60
            bg-gradient-to-br from-indigo-50 via-white to-purple-50
            dark:from-indigo-900/20 dark:via-gray-800 dark:to-purple-900/20 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <BanknotesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
              <div className="text-[11px] sm:text-xs uppercase tracking-wider font-semibold text-indigo-700 dark:text-indigo-300">
                {stats?.label || selectedMonth?.label} · share in USD
              </div>
            </div>
            {loading && !stats ? (
              <div className="h-10 w-56 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
            ) : (
              <div className="text-3xl sm:text-5xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight tabular-nums">
                {usd(stats?.totalUsd || 0)}
              </div>
            )}
          </div>

          {/* Per-currency breakdown */}
          {loading && !stats ? (
            <div className="py-12 flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400">
              <ArrowPathIcon className="w-6 h-6 animate-spin" />
              <span className="text-sm">Loading {selectedMonth?.label}…</span>
            </div>
          ) : (
            <>
              {!hasActivity && (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-4 py-3
                  text-center text-xs text-gray-500 dark:text-gray-400">
                  No BetConstruct sports activity in {stats?.label || selectedMonth?.label}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {rows.map((row) => (
                  <CurrencyCard key={row.currency} row={row} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminStatistics;
